/**
 * Trovva — Pixel Setup Component
 *
 * Settings card for configuring the Trovva Pixel tracking.
 * Displays pixel_id, code snippet, and recent activity verification.
 *
 * Inline styles only, using theme from '../../lib/theme'.
 */

import { useState, useEffect } from 'react';
import { Code2, Copy, CheckCircle2, AlertCircle, Loader2, Eye, BarChart3, Settings } from 'lucide-react';
import { theme } from '../../lib/theme';
import { supabase } from '../../lib/supabase';

export default function PixelSetup() {
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [recentVisits, setRecentVisits] = useState([]);
  const [loadingVisits, setLoadingVisits] = useState(false);
  const [propertyPatterns, setPropertyPatterns] = useState('/listing/, /property/, /homes/');
  const [savingPatterns, setSavingPatterns] = useState(false);
  const [patternsSaved, setPatternsSaved] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error('Not authenticated');

        const r = await fetch('/api/settings?section=org', {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        const data = await r.json();
        if (r.ok) {
          setOrg(data.org);
          if (data.org.pixel_property_patterns) {
            setPropertyPatterns(data.org.pixel_property_patterns);
          }
        }
      } catch (e) {
        console.error('[PixelSetup] org load error:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Load recent visits to verify pixel is working
  const loadRecentVisits = async () => {
    setLoadingVisits(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const r = await fetch('/api/pixel-track', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await r.json();
      if (r.ok) {
        setRecentVisits(data.visits || []);
      }
    } catch (e) {
      console.error('[PixelSetup] visits load error:', e);
    } finally {
      setLoadingVisits(false);
    }
  };

  // Save property patterns
  const savePatterns = async () => {
    if (!org) return;
    setSavingPatterns(true);
    setPatternsSaved(false);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const r = await fetch('/api/settings?section=org', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...org, pixel_property_patterns: propertyPatterns }),
      });

      if (r.ok) {
        setPatternsSaved(true);
        setTimeout(() => setPatternsSaved(false), 2000);
      }
    } catch (e) {
      console.error('[PixelSetup] save error:', e);
    } finally {
      setSavingPatterns(false);
    }
  };

  // Copy snippet to clipboard
  const copySnippet = () => {
    if (!org) return;
    const snippet = `<script src="https://app.trovva.ai/trovva-pixel.js" data-pixel-id="${org.pixel_id}" async></script>`;
    navigator.clipboard.writeText(snippet).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // ── Styles ────────────────────────────────────────────
  const cardStyle = {
    background: theme.colors.card,
    borderRadius: theme.radius.lg,
    border: `1px solid ${theme.colors.border}`,
    padding: 24,
    marginBottom: 20,
  };

  const headingStyle = {
    fontSize: 16,
    fontWeight: 700,
    color: theme.colors.text,
    marginBottom: 4,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  };

  const descStyle = {
    fontSize: 13,
    color: theme.colors.textLight,
    marginBottom: 16,
  };

  const sectionStyle = {
    marginBottom: 20,
  };

  const labelStyle = {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: theme.colors.text,
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  };

  const snippetBoxStyle = {
    background: theme.colors.bg,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.radius.sm,
    padding: 12,
    fontFamily: 'monospace',
    fontSize: 11,
    color: theme.colors.text,
    wordBreak: 'break-all',
    position: 'relative',
    marginBottom: 8,
  };

  const buttonStyle = {
    padding: '8px 16px',
    background: theme.colors.primary,
    color: '#fff',
    border: 'none',
    borderRadius: theme.radius.sm,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    transition: 'all 0.15s',
  };

  const buttonSecondaryStyle = {
    padding: '8px 16px',
    background: 'transparent',
    color: theme.colors.text,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.radius.sm,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    transition: 'all 0.15s',
  };

  const inputStyle = {
    width: '100%',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.radius.sm,
    padding: '8px 12px',
    fontSize: 13,
    color: theme.colors.text,
    background: theme.colors.bg,
    outline: 'none',
    boxSizing: 'border-box',
  };

  const visitItemStyle = {
    padding: 10,
    background: theme.colors.bg,
    borderRadius: 6,
    border: `1px solid ${theme.colors.border}`,
    fontSize: 11,
    marginBottom: 6,
  };

  const statusBadgeStyle = (active) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '4px 10px',
    borderRadius: 20,
    background: active ? '#ECFDF5' : '#FEF2F2',
    color: active ? '#10B981' : '#EF4444',
    fontSize: 12,
    fontWeight: 600,
  });

  if (loading) {
    return (
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32, color: theme.colors.textLight }}>
          <Loader2 size={16} style={{ animation: 'spin 1s linear infinite', marginRight: 8 }} />
          Loading pixel settings…
        </div>
      </div>
    );
  }

  if (!org) {
    return (
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 12, background: '#FEF2F2', borderRadius: 8 }}>
          <AlertCircle size={16} color={theme.colors.danger} />
          <span style={{ fontSize: 12, color: theme.colors.danger }}>Failed to load organization settings</span>
        </div>
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      {/* Header */}
      <div style={headingStyle}>
        <Eye size={18} color={theme.colors.primary} /> Trovva Pixel
      </div>
      <p style={descStyle}>
        Track visitor activity on your website and link it back to contacts in your CRM.
      </p>

      {/* Status */}
      <div style={{ marginBottom: 20 }}>
        <div style={labelStyle}>Status</div>
        <div style={statusBadgeStyle(true)}>
          <span style={{ width: 8, height: 8, background: '#10B981', borderRadius: 4 }} />
          Active
        </div>
      </div>

      {/* Pixel ID */}
      <div style={sectionStyle}>
        <div style={labelStyle}>Your Pixel ID</div>
        <div style={{ ...snippetBoxStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 12 }}>
          <code style={{ fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all' }}>
            {org.pixel_id}
          </code>
        </div>
      </div>

      {/* Installation snippet */}
      <div style={sectionStyle}>
        <div style={labelStyle}>Installation Code</div>
        <p style={{ fontSize: 12, color: theme.colors.textLight, marginBottom: 8 }}>
          Add this code to the <code style={{ background: theme.colors.bg, padding: '2px 4px', borderRadius: 3 }}>&lt;head&gt;</code> of your website:
        </p>
        <div style={snippetBoxStyle}>
          {`<script src="https://app.trovva.ai/trovva-pixel.js" data-pixel-id="${org.pixel_id}" async></script>`}
        </div>
        <button onClick={copySnippet} style={buttonStyle}>
          {copied ? (
            <>
              <CheckCircle2 size={14} /> Copied!
            </>
          ) : (
            <>
              <Copy size={14} /> Copy Code
            </>
          )}
        </button>
      </div>

      {/* Property URL patterns */}
      <div style={sectionStyle}>
        <div style={labelStyle}>Property Page Detection</div>
        <p style={{ fontSize: 12, color: theme.colors.textLight, marginBottom: 8 }}>
          Comma-separated URL patterns to detect property pages (e.g., /listing/, /property/, /homes/):
        </p>
        <textarea
          value={propertyPatterns}
          onChange={(e) => setPropertyPatterns(e.target.value)}
          style={{
            ...inputStyle,
            fontFamily: 'monospace',
            minHeight: 80,
            resize: 'vertical',
            padding: 12,
          }}
          placeholder="/listing/, /property/, /homes/"
        />
        <button
          onClick={savePatterns}
          disabled={savingPatterns}
          style={{
            ...buttonStyle,
            opacity: savingPatterns ? 0.6 : 1,
            pointerEvents: savingPatterns ? 'none' : 'auto',
            marginTop: 8,
          }}
        >
          {savingPatterns ? (
            <>
              <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Saving…
            </>
          ) : patternsSaved ? (
            <>
              <CheckCircle2 size={14} /> Saved
            </>
          ) : (
            <>
              <Settings size={14} /> Save Patterns
            </>
          )}
        </button>
      </div>

      {/* Instructions */}
      <div style={{
        padding: 12,
        background: `${theme.colors.info}10`,
        borderRadius: 8,
        border: `1px solid ${theme.colors.info}30`,
        marginBottom: 20,
      }}>
        <div style={{ fontSize: 12, color: theme.colors.info, fontWeight: 600, marginBottom: 6 }}>
          How it works:
        </div>
        <ul style={{ fontSize: 11, color: theme.colors.text, margin: 0, paddingLeft: 20, lineHeight: 1.6 }}>
          <li>When a lead clicks a link from your Trovva email or SMS, they're marked with a tracking token (tvid)</li>
          <li>The pixel automatically links subsequent visits to their contact record</li>
          <li>Visit history appears in the contact's timeline in Trovva</li>
          <li>Property pages are automatically detected and highlighted</li>
        </ul>
      </div>

      {/* Recent visits verification */}
      <div style={sectionStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <div style={labelStyle}>Recent Activity (Verification)</div>
          <button
            onClick={loadRecentVisits}
            disabled={loadingVisits}
            style={{ ...buttonSecondaryStyle, padding: '6px 12px', fontSize: 12 }}
          >
            {loadingVisits ? (
              <>
                <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
              </>
            ) : (
              <>
                <BarChart3 size={12} /> Refresh
              </>
            )}
          </button>
        </div>

        {recentVisits.length === 0 ? (
          <div style={{
            padding: 12,
            background: theme.colors.bg,
            borderRadius: 6,
            border: `1px dashed ${theme.colors.border}`,
            fontSize: 12,
            color: theme.colors.textLight,
          }}>
            No tracked visits yet. Add the pixel code to your website to see activity here.
          </div>
        ) : (
          <div style={{ maxHeight: 300, overflowY: 'auto' }}>
            {recentVisits.slice(0, 10).map((visit, idx) => (
              <div key={idx} style={visitItemStyle}>
                <div style={{ fontWeight: 600, color: theme.colors.text, marginBottom: 4, wordBreak: 'break-word' }}>
                  {visit.title || 'Untitled'}
                </div>
                <div style={{ fontSize: 10, color: theme.colors.textLight, wordBreak: 'break-word' }}>
                  {visit.url}
                </div>
                <div style={{ fontSize: 10, color: theme.colors.textLight, marginTop: 4 }}>
                  {new Date(visit.created_at).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
